import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { UnitWorkshop, Unit } from '@/types'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Video,
  AlertCircle,
  Calendar,
  Users,
  Clock
} from 'lucide-react'

interface WorkshopWithRelations extends Omit<UnitWorkshop, 'teacher'> {
  unit: Unit
  teacher: { first_name: string; last_name: string } | null
  enrollments_count: number
}

export default function WorkshopsList() {
  const [workshops, setWorkshops] = useState<WorkshopWithRelations[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; workshop: WorkshopWithRelations | null }>({
    open: false,
    workshop: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    loadWorkshops()
  }, [])

  const loadWorkshops = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_workshops')
        .select(`
          *,
          unit:units(*),
          cohort:cohorts(id, name)
        `)
        .order('scheduled_date', { ascending: true })

      if (error) throw error

      // Cargar información del profesor por separado si existe teacher_id
      const workshopsWithRelations = await Promise.all(
        (data || []).map(async (workshop: any) => {
          let teacher = null

          if (workshop.teacher_id) {
            const { data: teacherData } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', workshop.teacher_id)
              .single()
            teacher = teacherData
          }

          return {
            ...workshop,
            title: workshop.title || workshop.title_es || workshop.title_en || workshop.name_es || workshop.name_en || '',
            description: workshop.description || workshop.description_es || workshop.description_en || '',
            teacher,
            enrollments_count: 0, // Por ahora sin conteo de inscripciones
            unit: workshop.unit ? {
              ...workshop.unit,
              title: workshop.unit.title || workshop.unit.title_es || workshop.unit.title_en || workshop.unit.name_es || workshop.unit.name_en || '',
              order_num: workshop.unit.order_num ?? workshop.unit.unit_number ?? 0,
            } : null
          }
        })
      )

      setWorkshops(workshopsWithRelations)
    } catch (error) {
      console.error('Error loading workshops:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.workshop) return

    setIsDeleting(true)
    try {
      const { error } = await supabase
        .from('unit_workshops')
        .delete()
        .eq('id', deleteModal.workshop.id)

      if (error) throw error

      setWorkshops(workshops.filter(w => w.id !== deleteModal.workshop?.id))
      setDeleteModal({ open: false, workshop: null })
    } catch (error) {
      console.error('Error deleting workshop:', error)
      alert('Error al eliminar el taller')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredWorkshops = workshops.filter(workshop =>
    (workshop.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (workshop.unit?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (workshop: WorkshopWithRelations) => {
    const now = new Date()
    const scheduledDate = new Date(workshop.scheduled_date)

    if (scheduledDate > now) {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0055A4]/10 text-[#003d7a]">Programado</span>
    } else if (workshop.is_active) {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Completado</span>
    } else {
      return <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactivo</span>
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Talleres</h1>
          <p className="text-gray-600">Gestiona los talleres en vivo</p>
        </div>
        <Link
          to="/admin/workshops/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuevo Taller
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar talleres..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4]"
        />
      </div>

      {/* Workshops Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredWorkshops.length > 0 ? (
          filteredWorkshops.map((workshop) => (
            <div key={workshop.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-lg bg-indigo-100">
                    <Video className="w-6 h-6 text-indigo-600" />
                  </div>
                  {getStatusBadge(workshop)}
                </div>

                <h3 className="font-semibold text-gray-900 mb-1">{workshop.title}</h3>
                {(workshop as any).cohort?.name && (
                  <p className="text-xs text-indigo-600 font-medium mb-1">
                    {(workshop as any).cohort.name}
                  </p>
                )}
                <p className="text-sm text-gray-500 mb-4">
                  Unidad {workshop.unit?.order_num}: {workshop.unit?.title}
                </p>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(workshop.scheduled_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{workshop.duration_minutes} minutos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>
                      {workshop.enrollments_count}
                      {workshop.max_participants && ` / ${workshop.max_participants}`} inscritos
                    </span>
                  </div>
                  {workshop.teacher && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">Profesor:</span>
                      <span>{workshop.teacher.first_name} {workshop.teacher.last_name}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className={`text-xs font-medium ${
                    workshop.is_mandatory ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {workshop.is_mandatory ? 'Obligatorio' : 'Opcional'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Link
                      to={`/admin/workshops/${workshop.id}`}
                      className="p-2 text-gray-500 hover:text-[#0055A4] hover:bg-[#0055A4]/5 rounded-lg transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                    <Link
                      to={`/admin/workshops/${workshop.id}/edit`}
                      className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setDeleteModal({ open: true, workshop })}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No se encontraron talleres</p>
            <Link
              to="/admin/workshops/new"
              className="text-[#0055A4] hover:underline text-sm mt-2 inline-block"
            >
              Crear primer taller
            </Link>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-100">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Taller</h3>
            </div>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar el taller "{deleteModal.workshop?.title}"?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, workshop: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
